import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceCalendar } from '@/components/AbsenceApplication/AbsenceCalendar';
import { AbsenceDateField } from '@/components/AbsenceApplication/AbsenceDateField';
import { CalendarSheet } from '@/components/CalendarSheet';
import { Calendar } from '@/components/Icons/Calendar';
import { Radio } from '@/components/Radio';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  createCustomPeriod,
  formatPeriodRange,
  isSamePeriod,
} from '@/pages/Overview/period';
import type { StudyYearPeriod } from '@/types/overview';

enum DateField {
  From = 'from',
  To = 'to',
}

interface Props {
  studyYearPeriod: StudyYearPeriod;
  value: StudyYearPeriod | null;
  onChange: (period: StudyYearPeriod) => void;
  onCalendarBottomChange?: (bottom: number | null) => void;
}

export const CustomPeriodCard: React.FC<Props> = ({
  studyYearPeriod,
  value,
  onChange,
  onCalendarBottomChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [editedField, setEditedField] = useState(DateField.From);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isSelected = !!value;
  const isCalendarVisible = isSelected && isCalendarOpen;
  const { from, to } = value ?? studyYearPeriod;
  const hasOwnDates = !!value && !isSamePeriod(value, studyYearPeriod);
  const isEditingFrom = editedField === DateField.From;

  const calendarProps = {
    filledDate: isEditingFrom ? from : to,
    minDate: isEditingFrom ? studyYearPeriod.from : from,
    maxDate: isEditingFrom ? to : studyYearPeriod.to,
  };

  const handleSelect = () => onChange(value ?? studyYearPeriod);

  const handleFieldClick = (field: DateField) => {
    const isSameFieldOpen = isCalendarVisible && editedField === field;

    setEditedField(field);
    setIsCalendarOpen(!isSameFieldOpen);
  };

  const handleDateChange = (date: Date) => {
    setIsCalendarOpen(false);

    if (isEditingFrom) {
      onChange(createCustomPeriod(date, to));
      return;
    }

    onChange(createCustomPeriod(from, date));
  };

  const calendar = (
    <div
      ref={calendarRef}
      className={`absolute top-full z-20 mt-2 w-[375px] max-w-full rounded-xl bg-card px-4 py-3.5 shadow-card ${isEditingFrom ? 'left-0' : 'right-0'}`}
    >
      <AbsenceCalendar
        hasFixedWeekCount
        key={editedField}
        {...calendarProps}
        onChange={handleDateChange}
      />
    </div>
  );

  useCloseOnBreakpointChange(isCalendarOpen, () => setIsCalendarOpen(false));

  useEffect(() => {
    onCalendarBottomChange?.(
      calendarRef.current?.getBoundingClientRect().bottom ?? null,
    );
  }, [isCalendarVisible, isMobile, editedField, onCalendarBottomChange]);

  useEffect(() => {
    if (!isCalendarVisible || isMobile) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsCalendarOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isCalendarVisible, isMobile]);

  return (
    <div ref={containerRef} className="relative flex flex-col">
      <div className="flex flex-col rounded-xl bg-popover px-4 py-3 shadow-card">
        <button
          type="button"
          onClick={handleSelect}
          className="flex min-w-0 cursor-pointer items-center gap-3 text-left"
        >
          <span className="flex items-center justify-center rounded-full bg-info-surface p-2 text-primary">
            <Calendar className="size-4" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-subhead-14 truncate text-foreground">
              {t('overview.attendance.period.custom')}
            </p>
            <p className="text-body-13 truncate text-muted-foreground md:text-caption-12">
              {hasOwnDates
                ? formatPeriodRange(value)
                : t('overview.attendance.period.customHint')}
            </p>
          </div>
          <Radio checked={isSelected} />
        </button>

        <div
          inert={!isSelected}
          className={`grid transition-[grid-template-rows] duration-300 ease-out ${isSelected ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex items-center gap-3 pt-3">
              <AbsenceDateField
                hasChevron={!isMobile}
                label={t('absenceApplication.from')}
                value={from}
                isActive={isCalendarVisible && isEditingFrom}
                onClick={() => handleFieldClick(DateField.From)}
              />
              <span className="text-body-14 text-subtle-foreground">–</span>
              <AbsenceDateField
                hasChevron={!isMobile}
                label={t('absenceApplication.to')}
                value={to}
                isActive={isCalendarVisible && !isEditingFrom}
                onClick={() => handleFieldClick(DateField.To)}
              />
            </div>
          </div>
        </div>
      </div>

      {!isMobile && isCalendarVisible && calendar}

      {isMobile && (
        <CalendarSheet
          hasFixedWeekCount
          isOpen={isCalendarVisible}
          {...calendarProps}
          onChange={handleDateChange}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
    </div>
  );
};
